import React, { createContext, ReactElement, useContext, useReducer } from 'react';

import { ConfigFieldType } from 'adapter/in/common/form/types';

export enum PANEL {
    MAIN = 'MAIN',
    DATA_SET = 'DATA_SET',
    CONFIGURATION = 'CONFIGURATION',
    DATA_OPTION = 'DATA_OPTION',
}

export type PanelType = PANEL;

export enum ACTION_TYPE {
    SET_PANEL = 'SET_PANEL',
    EDIT_WIDGET = 'EDIT_WIDGET',
    SET_STATUS = 'SET_STATUS',
    RESET_WIDGET_MENU = 'RESET_WIDGET_MENU',
    TOGGLE_SAVE_BUTTON = 'TOGGLE_SAVE_BUTTON',
    DISABLE_SAVE_BUTTON = 'DISABLE_SAVE_BUTTON',
}

export enum WIDGET_STATUS {
    LOADING = 'LOADING',
    ERROR = 'ERROR',
    IN_PROGRESS = 'IN_PROGRESS',
    CREATED = 'CREATED',
    UPDATED = 'UPDATED',
    EDITING = 'EDITING',
}

export type NONE = 'NONE';

export type WidgetStatusType = WIDGET_STATUS;

export const initialState: WidgetMenuStateType = {
    panel: PANEL.MAIN,
    status: WIDGET_STATUS.IN_PROGRESS,
    isSaveButtonShown: false,
    isSaveButtonDisabled: false,
    tab: 'DATA',
};

type SetPanel = {
    type: ACTION_TYPE.SET_PANEL;
    payload: PanelType;
};

type EditWidget = {
    type: ACTION_TYPE.EDIT_WIDGET;
    payload: ConfigFieldType;
};

type SetStatus = {
    type: ACTION_TYPE.SET_STATUS;
    payload: WidgetStatusType;
};

type ToggleSaveButton = {
    type: ACTION_TYPE.TOGGLE_SAVE_BUTTON;
    payload: boolean;
};

type SetDisableSaveButtonTo = {
    type: ACTION_TYPE.DISABLE_SAVE_BUTTON;
    payload: boolean;
};

type ResetWidgetMenu = {
    type: ACTION_TYPE.RESET_WIDGET_MENU;
};

export type WidgetMenuStateType = {
    panel: PanelType;
    status: WidgetStatusType;
    isSaveButtonShown: boolean;
    isSaveButtonDisabled: boolean;
    tab: string;
};

export type WidgetMenuDispatchType = {
    setPanel: (panel: PanelType) => void;
    editWidget: (tab: ConfigFieldType) => void;
    setStatus: (status: WidgetStatusType) => void;
    resetWidgetMenu: () => void;
    toggleSaveButton: (status: boolean) => void;
    setDisableSaveButtonTo: (status: boolean) => void;
};

type WidgetMenuActionType = SetPanel | EditWidget | SetStatus | ResetWidgetMenu | ToggleSaveButton | SetDisableSaveButtonTo;

export const useCurrentWidgetConfigurationReducer = (
    state: WidgetMenuStateType,
    action: WidgetMenuActionType
    // eslint-disable-next-line consistent-return
): WidgetMenuStateType => {
    // Todo why need payload?
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
        case ACTION_TYPE.SET_PANEL: {
            return {
                ...state,
                panel: payload as PanelType,
            };
        }
        case ACTION_TYPE.SET_STATUS: {
            return {
                ...state,
                status: payload as WidgetStatusType,
            };
        }
        case ACTION_TYPE.EDIT_WIDGET: {
            return {
                ...state,
                status: WIDGET_STATUS.EDITING,
                panel: PANEL.CONFIGURATION,
                tab: payload as ConfigFieldType,
            };
        }
        case ACTION_TYPE.TOGGLE_SAVE_BUTTON: {
            return {
                ...state,
                isSaveButtonShown: payload,
            };
        }
        case ACTION_TYPE.DISABLE_SAVE_BUTTON: {
            return {
                ...state,
                isSaveButtonDisabled: payload,
            };
        }
        case ACTION_TYPE.RESET_WIDGET_MENU: {
            return initialState;
        }
    }
};

type WidgetMenuProviderType = {
    children: ReactElement;
};

export const WidgetMenuDispatchContext = createContext({} as WidgetMenuDispatchType);
export const WidgetMenuStateContext = createContext({} as WidgetMenuStateType);

const dispatchWidgetMenuView = (): WidgetMenuDispatchType => useContext(WidgetMenuDispatchContext);
const useWidgetMenuView = (): WidgetMenuStateType => useContext(WidgetMenuStateContext);

const WidgetMenuViewProvider = (props: WidgetMenuProviderType): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useCurrentWidgetConfigurationReducer, initialState);

    const setPanel = (panel: PanelType) =>
        dispatch({
            type: ACTION_TYPE.SET_PANEL,
            payload: panel,
        });

    const editWidget = (tab: ConfigFieldType) =>
        dispatch({
            type: ACTION_TYPE.EDIT_WIDGET,
            payload: tab,
        });

    const setStatus = (status: WidgetStatusType) =>
        dispatch({
            type: ACTION_TYPE.SET_STATUS,
            payload: status,
        });

    const resetWidgetMenu = () =>
        dispatch({
            type: ACTION_TYPE.RESET_WIDGET_MENU,
        });

    const toggleSaveButton = (showSaveButton: boolean) => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_SAVE_BUTTON,
            payload: showSaveButton,
        });
    };

    const setDisableSaveButtonTo = (status: boolean) => {
        dispatch({
            type: ACTION_TYPE.DISABLE_SAVE_BUTTON,
            payload: status,
        });
    };

    return (
        <WidgetMenuStateContext.Provider value={state}>
            <WidgetMenuDispatchContext.Provider
                value={{
                    setPanel,
                    editWidget,
                    setStatus,
                    resetWidgetMenu,
                    toggleSaveButton,
                    setDisableSaveButtonTo,
                }}
            >
                {children}
            </WidgetMenuDispatchContext.Provider>
        </WidgetMenuStateContext.Provider>
    );
};

export { WidgetMenuViewProvider, dispatchWidgetMenuView, useWidgetMenuView };
